import type { AdminUserListItemResponse } from "../../../types/adminTypes";
import { formatDate } from "../../../utils/DateUtils";
import { formatCityStateZip } from "../../../utils/LocationUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";

type AdminUsersTableRowProps = {
  user: AdminUserListItemResponse;
  onView: (user: AdminUserListItemResponse) => void;
};

const statusClassName: Record<AdminUserListItemResponse["status"], string> = {
  ACTIVE: "success",
  PENDING: "warning",
  SUSPENDED: "warning",
  DELETED: "danger",
};

const getTeenStripeLabel = (user: AdminUserListItemResponse) => {
  if (!user.roles.includes("TEEN")) {
    return "N/A";
  }
  return user.teenPayoutReady ? "Ready" : "Not ready";
};

const getTeenStripeTone = (user: AdminUserListItemResponse): "neutral" | "success" | "warning" => {
  if (!user.roles.includes("TEEN")) {
    return "neutral";
  }
  return user.teenPayoutReady ? "success" : "warning";
};

const AdminUsersTableRow = ({ user, onView }: AdminUsersTableRowProps) => {
  const isDeleted = user.status === "DELETED";
  const formerName = [user.deletionAudit?.firstName, user.deletionAudit?.lastName].filter(Boolean).join(" ");
  const displayName = isDeleted
    ? `Deleted account${formerName ? ` (${formerName})` : ""}`
    : `${user.firstName} ${user.lastName}`.trim();
  const displayEmail = isDeleted ? user.deletionAudit?.email ?? "Account email removed" : user.email;
  const deletionRoles = user.deletionAudit?.roles?.length ? user.deletionAudit.roles : user.roles;

  return (
    <tr className="align-top">
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="space-y-1">
          <p className="font-semibold">{displayName}</p>
          <p className="text-myteengig-text-muted">{displayEmail}</p>
          <p className="text-xs text-myteengig-text-muted">
            {isDeleted ? `User ID ${user.id.slice(0, 8)}` : user.emailVerified ? "Email verified" : "Email unverified"}
          </p>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="flex flex-wrap gap-2">
          {deletionRoles.map((role) => (
            <AdminPillBadge key={role}>{role}</AdminPillBadge>
          ))}
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="space-y-2">
          <AdminPillBadge tone={statusClassName[user.status] as "success" | "warning" | "danger"}>
            {user.status}
          </AdminPillBadge>
          {user.deletedAt ? <p className="text-xs text-myteengig-text-muted">Deleted {formatDate(user.deletedAt)}</p> : null}
          {user.deletionAudit?.deletionSource ? (
            <p className="text-xs text-myteengig-text-muted">Source {user.deletionAudit.deletionSource}</p>
          ) : null}
          <p className="text-xs text-myteengig-text-muted">Updated {formatDate(user.updatedAt)}</p>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="space-y-2">
          <AdminPillBadge tone={getTeenStripeTone(user)}>{getTeenStripeLabel(user)}</AdminPillBadge>
          {user.roles.includes("TEEN") ? (
            <p className="text-xs text-myteengig-text-muted">
              {user.teenPayoutOnboardingStatus ?? "No profile"}
            </p>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        {formatCityStateZip(user, "No location")}
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">{formatDate(user.createdAt)}</td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <Button variant="outline" size="sm" onClick={() => onView(user)}>
          View
        </Button>
      </td>
    </tr>
  );
};

export default AdminUsersTableRow;
